import {
  SegmentedControl,
  Group,
  NumberInput,
  Select,
  Text,
  Stack,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { DateInputWrapper } from './DateInputWrapper';

// -----------------------------------------------------------------------------
export type MeasurementAgeType = 'today' | 'age' | 'date';

export interface MeasurementAge {
  type: MeasurementAgeType;
  // If type === 'age'
  ageValue?: number;
  ageUnit?: 'months' | 'years';
  // If type === 'date'
  date?: Date | null;
}

// -----------------------------------------------------------------------------
export interface DateOrAgeSelectorProps {
  value: MeasurementAge;
  onChange: (age: MeasurementAge) => void;
}

// -----------------------------------------------------------------------------
export const DateOrAgeSelector = ({
  value,
  onChange,
}: DateOrAgeSelectorProps) => {
  const { t } = useTranslation(['patient-pages']);

  const handleTypeChange = (type: string) => {
    if (type === 'today') {
      onChange({ type: 'today' });
    } else if (type === 'age') {
      onChange({
        type: 'age',
        ageValue: value.ageValue || undefined,
        ageUnit: value.ageUnit || 'months',
      });
    } else if (type === 'date') {
      onChange({
        type: 'date',
        date: value.date || null,
      });
    }
  };

  const handleAgeValueChange = (ageValue: number | string) => {
    const numValue =
      typeof ageValue === 'string' ? parseFloat(ageValue) : ageValue;
    onChange({
      ...value,
      ageValue: isNaN(numValue) ? undefined : numValue,
    });
  };

  const handleAgeUnitChange = (ageUnit: string | null) => {
    if (ageUnit === 'months' || ageUnit === 'years') {
      onChange({
        ...value,
        ageUnit,
      });
    }
  };

  const handleDateChange = (date: Date | null) => {
    onChange({
      ...value,
      date,
    });
  };

  return (
    <Group gap="xs" grow>
      <Stack gap={2}>
        <Text size="sm" fw={500}>
          {t('physicalGrowth.measurementAge.title', {
            ns: 'patient-pages',
            defaultValue: 'Age of Measurements',
          })}
        </Text>
        <SegmentedControl
          value={value.type}
          onChange={handleTypeChange}
          data={[
            {
              label: t('physicalGrowth.measurementAge.today', {
                ns: 'patient-pages',
                defaultValue: 'Today',
              }),
              value: 'today',
            },
            {
              label: t('physicalGrowth.measurementAge.age', {
                ns: 'patient-pages',
                defaultValue: 'Age',
              }),
              value: 'age',
            },
            {
              label: t('physicalGrowth.measurementAge.date', {
                ns: 'patient-pages',
                defaultValue: 'Date',
              }),
              value: 'date',
            },
          ]}
          size="sm"
        />
      </Stack>

      {value.type === 'age' && (
        <Group gap="xs" align="flex-end">
          <NumberInput
            label={t('physicalGrowth.measurementAge.ageValue', {
              ns: 'patient-pages',
              defaultValue: 'Age',
            })}
            value={value.ageValue}
            onChange={handleAgeValueChange}
            min={0}
            max={value.ageUnit === 'months' ? 24 : 110}
            placeholder="0"
            hideControls
            flex={1}
          />
          <Select
            label={t('physicalGrowth.measurementAge.ageUnit', {
              ns: 'patient-pages',
              defaultValue: 'Unit',
            })}
            value={value.ageUnit || 'months'}
            onChange={handleAgeUnitChange}
            data={[
              {
                value: 'months',
                label: t('physicalGrowth.measurementAge.months', {
                  ns: 'patient-pages',
                  defaultValue: 'Months',
                }),
              },
              {
                value: 'years',
                label: t('physicalGrowth.measurementAge.years', {
                  ns: 'patient-pages',
                  defaultValue: 'Years',
                }),
              },
            ]}
          />
        </Group>
      )}

      {value.type === 'date' && (
        <DateInputWrapper
          label={t('physicalGrowth.measurementAge.dateLabel', {
            ns: 'patient-pages',
            defaultValue: 'Measurement Date',
          })}
          value={value.date || undefined}
          onChange={handleDateChange}
          maxDate={new Date()}
        />
      )}
    </Group>
  );
};
